/**
 * PermitsSection — project-scoped Permit Tracker.
 *
 * Renders in two states:
 *   'home'     — root:permits node in the multi-project home view
 *   'rootZoom' — full permit list when Permits is the zoom root
 *
 * Hierarchy: Permit → Agency Reviews → Activity Log (+ "Log activity").
 * Status is Airtable-driven: logging an activity event triggers an automation
 * that sets the agency-review status; this UI only logs events and reads status.
 *
 * Caches (project-scoped, reset on project change, loaded as soon as a project is
 * open regardless of whether this section is ever expanded — this component is
 * always mounted, so MilestoneSections.jsx's Permits branch under permit-related
 * milestones reads these same caches read-only rather than fetching its own copy):
 *   ctx.permitCache         — normalized permits     (undefined|null|[])
 *   ctx.permitReviewCache   — normalized reviews      (undefined|null|[])
 *   ctx.permitActivityCache — { [reviewId]: activity } (undefined|null|[])
 */

import { useState, useEffect, useReducer, useRef } from 'react'
import { wf, saveViewState } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider } from './ActionMenu.jsx'
import {
  getProjectPermits,
  getProjectAgencyReviews,
  getPermitActivity,
  getDeltaRevisionsForProject,
  deletePermit,
  deleteAgencyReview,
  deletePermitActivity,
} from '../services/permits.js'
import { getPortalForPermit } from '../services/portals.js'
import SlackThreadPanel from './SlackThreadPanel.jsx'
import { confirmDialog, alertDialog } from './Dialog.jsx'
import { DeltaRevisionsSection, AgencyCorrespondenceSection, StakeholderInputSection } from './PlanCheckSections.jsx'
import { ReviewClockChip, openReviewClock } from './ReviewClock.jsx'
import { loadReviewClocks, shouldPromptClock } from '../services/reviewClock.js'

/* ⚽ Easter egg — bounces once per click */
function SoccerBall() {
  const [bouncing, setBouncing] = useState(false)
  return (
    <span
      title="We have the ball"
      className={'wf-soccer-ball' + (bouncing ? ' wf-soccer-ball--bouncing' : '')}
      style={{ marginRight: 2 }}
      onClick={e => { e.stopPropagation(); setBouncing(false); requestAnimationFrame(() => setBouncing(true)) }}
      onAnimationEnd={() => setBouncing(false)}
    >⚽</span>
  )
}

async function confirmDelete(msg, fn, after) {
  if (!(await confirmDialog({ title: 'Delete?', message: msg, confirmLabel: 'Delete', danger: true }))) return
  Promise.resolve(fn())
    .then(() => { after && after(); window.dispatchEvent(new Event('outline-render')) })
    .catch(e => alertDialog({ title: 'Delete failed', message: e.message || String(e) }))
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

function fmtDate(d) {
  const dp = (d || '').split('-')
  if (dp.length === 3) return MONTHS[parseInt(dp[1]) - 1] + ' ' + parseInt(dp[2]) + ', ' + dp[0]
  return d || ''
}

// Agency-review status → color (plain status strings, no emoji)
function reviewStatusColor(status) {
  if (!status) return 'var(--aj-text-muted)'
  if (status.includes('Approved'))   return '#1D9E75'
  if (status.includes('Cancelled'))  return 'var(--aj-text-muted)'
  if (status.includes('Corrections'))return '#d97706'
  if (status.includes('Items'))      return '#dc2626'
  if (status.includes('Hearing'))    return '#7c3aed'
  if (status.includes('Appeal'))     return '#dc2626'
  return '#2563eb'  // submitted / under review
}

function loadingRow(label) {
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ color: 'var(--aj-text-muted)', fontStyle: label === 'loading' ? 'normal' : 'italic' }}>
            {label === 'loading' ? 'Loading…' : label === 'empty' ? 'No permits yet.' : label}
          </span>
        </div>
      </div>
    </div>
  )
}

/* ── Visibility ─────────────────────────────────────────────────────────── */

function calcPermitsVis() {
  if (!wf.zoomProjectId) return 'hidden'
  const zs = wf.zoomSection || ''
  if (zs.startsWith('permit-detail:')) return 'permitZoom'
  if (zs.startsWith('review-detail:')) return 'reviewZoom'
  if (wf.zoomRoot === 'permits' && !wf.zoomSection && !wf.zoomPhaseId && !wf.zoomMsId &&
      !wf.zoomCat && !wf.zoomItemId && !wf.zoomConceptId && !wf.searchQuery) return 'rootZoom'
  if (wf.zoomRoot === 'reference' && !wf.zoomSection && !wf.zoomPhaseId && !wf.zoomMsId &&
      !wf.zoomCat && !wf.zoomItemId && !wf.zoomConceptId && !wf.searchQuery) return 'referenceZoom'
  if (!wf.zoomRoot && !wf.zoomSection && !wf.zoomPhaseId && !wf.zoomMsId &&
      !wf.zoomCat && !wf.zoomItemId && !wf.zoomConceptId && !wf.searchQuery) return 'home'
  return 'hidden'
}

/* ── Activity row (with edit/delete menu) ───────────────────────────────── */

function ActivityRow({ act, reviewId, reviewName }) {
  const menu = useActionMenu()
  function handleEdit() {
    window.dispatchEvent(new CustomEvent('open-log-permit-activity', { detail: {
      reviewId, reviewName, activityId: act.id,
      prefill: { type: act.type, date: act.date, remarks: act.remarks, plannedNext: act.plannedNext, plannedNextDate: act.plannedNextDate },
    } }))
  }
  function handleDelete() {
    confirmDelete('Delete this activity?', () => deletePermitActivity(act.id), () => {
      if (ctx.permitActivityCache) ctx.permitActivityCache[reviewId] = undefined
      ctx.permitReviewCache = undefined
    })
  }
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row" style={{ alignItems: 'flex-start' }}>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <span className="wf-bullet wf-bullet--item" style={{ marginTop: 6 }}></span>
        <div className="wf-text-wrap" style={{ minWidth: 0, flexDirection: 'column', alignItems: 'stretch' }}>
          {/* Top line: type · who · date — stays compact */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 0, width: '100%' }}>
            <span className="wf-node-text" style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <span style={{ fontWeight: 600 }}>{act.type || 'Activity'}</span>
              {act.recordedByName && (<><span style={{ color: 'var(--aj-border-mid)', margin: '0 4px' }}>|</span><span style={{ opacity: 0.55 }}>{act.recordedByName}</span></>)}
            </span>
            {act.date && (<span className="wf-date-btn" style={{ pointerEvents: 'none', marginLeft: 8, flexShrink: 0 }}>{fmtDate(act.date)}</span>)}
          </div>
          {/* Remarks: full text, wrapping (notes can be long) — same size as outline node text */}
          {act.remarks && (
            <div className="wf-node-text" style={{ color: 'var(--aj-text-mid)', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word', marginTop: 2, lineHeight: 1.45 }}>
              {act.remarks}
            </div>
          )}
          {/* Planned next activity + its date */}
          {act.plannedNext && (
            <div style={{ marginTop: 3, fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)' }}>
              <span style={{ letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginRight: 6 }}>Planned next</span>
              {act.plannedNext}{act.plannedNextDate ? ' · ' + fmtDate(act.plannedNextDate) : ''}
            </div>
          )}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={handleEdit}>Edit activity</ActionMenuItem>
        <ActionMenuItem onClick={handleDelete} danger>Delete activity</ActionMenuItem>
      </ActionMenu>
    </div>
  )
}

/* ── Activity Log (per review) ──────────────────────────────────────────── */

function ReviewActivitySection({ review, zoom = false }) {
  const reviewId = review.id
  const key = 'permit-acts:' + reviewId
  // In zoom mode the activity log is always open (it's the focused content).
  const open = zoom || wf.collapsed[key] === false
  const isCollapsed = !open
  const acts = ctx.permitActivityCache?.[reviewId]   // undefined=not started, null=in-flight, []=loaded
  const menu = useActionMenu()

  function fetchActs() {
    if (!ctx.permitActivityCache) ctx.permitActivityCache = {}
    ctx.permitActivityCache[reviewId] = null
    getPermitActivity(review.activityIds).then(list => {
      if (!ctx.permitActivityCache) ctx.permitActivityCache = {}
      ctx.permitActivityCache[reviewId] = list
      window.dispatchEvent(new Event('outline-render'))
    }).catch(() => {
      if (!ctx.permitActivityCache) ctx.permitActivityCache = {}
      ctx.permitActivityCache[reviewId] = []
      window.dispatchEvent(new Event('outline-render'))
    })
  }

  useEffect(() => {
    if (isCollapsed) return
    if (acts !== undefined && acts !== null) return
    fetchActs()
  }, [isCollapsed, reviewId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch after a cache bust (e.g. a new activity was logged)
  useEffect(() => {
    function onRender() {
      if (!zoom && wf.collapsed[key] !== false) return
      if (ctx.permitActivityCache?.[reviewId] === undefined) fetchActs()
    }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [reviewId, key, zoom]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleLog() {
    window.dispatchEvent(new CustomEvent('open-log-permit-activity', {
      detail: { reviewId, reviewName: review.name, permitId: review.permitId },
    }))
  }

  function toggleCollapse(e) {
    e.stopPropagation()
    wf.collapsed[key] = !isCollapsed
    window.dispatchEvent(new Event('outline-render'))
  }

  return (
    <div className={`wf-node wf-node--section${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        {zoom ? (
          <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        ) : (
          <button className="wf-triangle" onClick={toggleCollapse}>
            <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
          </button>
        )}
        <button className="wf-bullet wf-bullet--section" title="Activity Log"></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Activity Log</span>
          {Array.isArray(acts) && (
            <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: 8 }}>{acts.length}</span>
          )}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={handleLog}>Log Activity</ActionMenuItem>
      </ActionMenu>

      {!isCollapsed && (
        <div className="wf-children">
          {(acts === null || acts === undefined) && loadingRow('loading')}
          {Array.isArray(acts) && acts.length === 0 && (
            <div className="wf-node wf-node--item">
              <div className="wf-row">
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
                <div className="wf-text-wrap">
                  <span className="wf-node-text" style={{ color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>No activity logged yet.</span>
                </div>
              </div>
            </div>
          )}
          {Array.isArray(acts) && acts.map(act => (
            <ActivityRow key={act.id} act={act} reviewId={reviewId} reviewName={review.name} />
          ))}
        </div>
      )}
    </div>
  )
}

/* ── Review discussion (Slack thread) ────────────────────────────────────────
   Shared by AgencyReviewNode (row) and ReviewZoomContent (zoom). Renders a
   fragment: the Slack chat button + the slide-in SlackThreadPanel. The review's
   RASCI inline went with RASCI itself (docs/FLOW_RECONCILIATION_PLAN.md decision 5 —
   agency reviews get no assignment surface for now); the discussion stayed.
   The SlackThreadPanel is position:fixed, so its DOM location is irrelevant. */

function ReviewSlack({ review, pushRight = false }) {
  const projectId   = ctx.recordId || wf.zoomProjectId || ''
  const projectName = ctx.state?.milestones?.[0]?.fields?.['Project Name'] || ''
  const [showSlackPanel, setShowSlackPanel] = useState(false)
  const [localThreadTs,  setLocalThreadTs]  = useState(review.slackThreadTs || '')

  const threadTs   = localThreadTs
  const lastReadTs = threadTs ? localStorage.getItem('slack:read:' + review.id) : null
  const hasUnread  = !!(threadTs && lastReadTs !== threadTs)

  return (
    <>
      {projectId && (
        <button
          className={`wf-slack-btn${threadTs ? ' wf-slack-btn--active' : ''}`}
          title={threadTs ? 'View discussion' : 'Start a discussion in Slack'}
          onClick={e => { e.stopPropagation(); setShowSlackPanel(true) }}
          /* In row mode the chat button is shoved to the far right, matching the
             Drawing Sheet line-item layout; in the zoom "Discussion" line it sits
             inline next to the label. */
          style={pushRight ? { marginLeft: 'auto' } : { marginLeft: 0 }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          {hasUnread && <span className="wf-slack-unread-dot" />}
        </button>
      )}
      {showSlackPanel && projectId && (
        <SlackThreadPanel
          entityId={review.id}
          entityType="agency_review"
          entityName={(review.agencyType ? review.agencyType + ' — ' : '') + (review.agency || review.name || 'Agency Review')}
          projectId={projectId}
          projectName={projectName}
          threadTs={localThreadTs}
          onClose={() => setShowSlackPanel(false)}
          onThreadStarted={ts => setLocalThreadTs(ts)}
        />
      )}
    </>
  )
}

/* ── Agency Review node ─────────────────────────────────────────────────── */

function AgencyReviewNode({ review }) {
  const key = 'permit-review:' + review.id
  const isCollapsed = wf.collapsed[key] !== false
  const statusColor = reviewStatusColor(review.status)
  const menu = useActionMenu()
  const portalLoaded = useRef(false)

  // Lazy-load the portal for this review's permit when the review is expanded
  useEffect(() => {
    if (isCollapsed || !review.permitId) return
    if (portalLoaded.current) return
    const permitAtId = review.permitId
    if (ctx.portalCache?.[permitAtId] !== undefined) return
    portalLoaded.current = true
    if (!ctx.portalCache) ctx.portalCache = {}
    ctx.portalCache[permitAtId] = null
    getPortalForPermit(permitAtId).then(d => {
      if (!ctx.portalCache) ctx.portalCache = {}
      ctx.portalCache[permitAtId] = d.portal || null
      window.dispatchEvent(new Event('outline-render'))
    }).catch(() => {
      if (!ctx.portalCache) ctx.portalCache = {}
      ctx.portalCache[permitAtId] = null
    })
  }, [isCollapsed, review.permitId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Re-check after cache bust
  useEffect(() => {
    function onRender() {
      if (isCollapsed || !review.permitId) return
      if (ctx.portalCache?.[review.permitId] === undefined) {
        portalLoaded.current = false
      }
    }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [isCollapsed, review.permitId]) // eslint-disable-line react-hooks/exhaustive-deps

  const portal = review.permitId ? (ctx.portalCache?.[review.permitId] ?? undefined) : undefined

  function toggleCollapse(e) {
    e.stopPropagation()
    wf.collapsed[key] = !isCollapsed
    window.dispatchEvent(new Event('outline-render'))
  }

  function handleLog() {
    window.dispatchEvent(new CustomEvent('open-log-permit-activity', { detail: { reviewId: review.id, reviewName: review.name, permitId: review.permitId } }))
  }
  function handleSetStatus() {
    window.dispatchEvent(new CustomEvent('open-log-permit-activity', { detail: { reviewId: review.id, reviewName: review.name, permitId: review.permitId, mode: 'setStatus', currentStatus: review.status } }))
  }
  function handleEdit() {
    window.dispatchEvent(new CustomEvent('open-add-agency-review-modal', { detail: {
      reviewId: review.id,
      prefill: { agencyOrgId: review.agencyOrgId, trackingNumber: review.trackingNumber, status: review.status, milestoneIds: review.milestoneIds },
    } }))
  }
  function handleDelete() {
    confirmDelete('Delete this agency review and its activity?', () => deleteAgencyReview(review.id), () => { ctx.permitReviewCache = undefined })
  }
  function handleManagePortal() {
    const p = review.permitId ? ctx.portalCache?.[review.permitId] : null
    if (p) {
      window.dispatchEvent(new CustomEvent('open-portal-modal', { detail: {
        portalId: p.id,
        permitAirtableId: review.permitId,
        prefill: { jurisdiction_name: p.jurisdiction_name, state: p.state, url: p.url, username: p.username, password: p.password, notes: p.notes },
      } }))
    } else {
      window.dispatchEvent(new CustomEvent('open-portal-modal', { detail: { permitAirtableId: review.permitId } }))
    }
  }

  return (
    <div className={`wf-node wf-node--section${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--ms" title="Zoom into review"
          onClick={e => { e.stopPropagation(); ctx.zoomToSection && ctx.zoomToSection('review-detail:' + review.id, { root: 'permits' }) }}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text">
            {review.agencyType ? review.agencyType + ' — ' : ''}{review.agency || review.name}
          </span>
          <span className="wf-meta">
            {review.hasBall && <SoccerBall />}
            {review.status && (
              <span className="wf-status-pill" style={{ color: statusColor, borderColor: statusColor + '33' }}>
                {review.status}
              </span>
            )}
            {review.trackingNumber && (
              <span className="wf-date-btn" style={{ pointerEvents: 'none', color: 'var(--aj-text-muted)' }}>
                #{review.trackingNumber}
              </span>
            )}
            <ReviewClockChip reviewId={review.id} reviewName={review.agency || review.name}
              prompt={shouldPromptClock(review)} />
            {review.nextDeadline && (
              <span className="wf-date-btn" style={{ pointerEvents: 'none', color: review.daysUntilDeadline != null && review.daysUntilDeadline < 0 ? '#dc2626' : 'var(--aj-text-muted)' }}>
                {fmtDate(review.nextDeadline)}
              </span>
            )}
            {review.portalUrl && (
              <a href={review.portalUrl} target="_blank" rel="noreferrer"
                 className="wf-date-btn" style={{ textDecoration: 'none', color: '#2563eb' }}
                 onClick={e => e.stopPropagation()}>portal ↗</a>
            )}
          </span>
          <ReviewSlack review={review} pushRight />
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={handleLog}>Log activity</ActionMenuItem>
        <ActionMenuItem onClick={handleSetStatus}>Set status…</ActionMenuItem>
        <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('open-hearing-outcome', { detail: { reviewId: review.id } }))}>Record the hearing outcome…</ActionMenuItem>
        <ActionMenuItem onClick={() => openReviewClock(review.id, review.agency || review.name)}>Review clock…</ActionMenuItem>
        <ActionMenuItem onClick={() => { window.dispatchEvent(new CustomEvent('open-plan-check-letter', { detail: { reviewId: review.id, reviewName: review.agency || review.name } })) }}>Plan check cover letter…</ActionMenuItem>
        <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('open-neighbor-letter', { detail: { reviewId: review.id, reviewName: review.agency || review.name } }))}>Neighbor letter…</ActionMenuItem>
        <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('open-permit-report-card', { detail: {} }))}>Plan check report card…</ActionMenuItem>
        <ActionMenuItem onClick={handleEdit}>Edit review</ActionMenuItem>
        <ActionMenuItem onClick={handleManagePortal}>{portal ? 'Edit portal' : 'Add portal'}</ActionMenuItem>
        {(portal?.url || review.portalUrl) && <ActionMenuItem onClick={() => window.open(portal?.url || review.portalUrl, '_blank')}>Open portal</ActionMenuItem>}
        <ActionMenuDivider />
        <ActionMenuItem onClick={handleDelete} danger>Delete review</ActionMenuItem>
      </ActionMenu>
      {!isCollapsed && (
        <div className="wf-children">
          {/* Portal row — shown above Activity Log when a portal is linked */}
          {portal && (
            <div className="wf-node wf-node--item">
              <div className="wf-row">
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                <span className="wf-bullet wf-bullet--item"></span>
                <div className="wf-text-wrap" style={{ minWidth: 0 }}>
                  <span className="wf-node-text" style={{ fontSize: 11 }}>
                    <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginRight: 6 }}>Portal</span>
                    {portal.jurisdiction_name ? <span style={{ marginRight: 6 }}>{portal.jurisdiction_name}</span> : null}
                    {portal.url && <a href={portal.url} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontSize: 11 }} onClick={e => e.stopPropagation()}>↗ open</a>}
                  </span>
                  {portal.username && (
                    <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)', marginLeft: 'auto', flexShrink: 0 }}>
                      👤 {portal.username}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
          <AgencyCorrespondenceSection review={review} />
          <StakeholderInputSection review={review} />
          <ReviewActivitySection review={review} />
        </div>
      )}
    </div>
  )
}

/* ── Review zoom content — portal row + always-open Activity Log (children-only) ── */

function ReviewZoomContent({ review }) {
  const portalLoaded = useRef(false)
  useEffect(() => {
    if (!review.permitId || portalLoaded.current) return
    const permitAtId = review.permitId
    if (ctx.portalCache?.[permitAtId] !== undefined) return
    portalLoaded.current = true
    if (!ctx.portalCache) ctx.portalCache = {}
    ctx.portalCache[permitAtId] = null
    getPortalForPermit(permitAtId).then(d => {
      if (!ctx.portalCache) ctx.portalCache = {}
      ctx.portalCache[permitAtId] = d.portal || null
      window.dispatchEvent(new Event('outline-render'))
    }).catch(() => { if (!ctx.portalCache) ctx.portalCache = {}; ctx.portalCache[permitAtId] = null })
  }, [review.permitId])
  const portal = review.permitId ? (ctx.portalCache?.[review.permitId] ?? null) : null
  return (
    <>
      {/* Discussion line (Slack thread) */}
      <div className="wf-node wf-node--item">
        <div className="wf-row">
          <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
          <span className="wf-bullet wf-bullet--item"></span>
          <div className="wf-text-wrap" style={{ minWidth: 0 }}>
            <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginRight: 6 }}>Discussion</span>
            <ReviewSlack review={review} />
          </div>
        </div>
      </div>
      {portal && (
        <div className="wf-node wf-node--item">
          <div className="wf-row">
            <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
            <span className="wf-bullet wf-bullet--item"></span>
            <div className="wf-text-wrap" style={{ minWidth: 0 }}>
              <span className="wf-node-text" style={{ fontSize: 11 }}>
                <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginRight: 6 }}>Portal</span>
                {portal.jurisdiction_name ? <span style={{ marginRight: 6 }}>{portal.jurisdiction_name}</span> : null}
                {portal.url && <a href={portal.url} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontSize: 11 }} onClick={e => e.stopPropagation()}>↗ open</a>}
              </span>
              {portal.username && (
                <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)', marginLeft: 'auto', flexShrink: 0 }}>👤 {portal.username}</span>
              )}
            </div>
          </div>
        </div>
      )}
      <AgencyCorrespondenceSection review={review} />
      <StakeholderInputSection review={review} />
      <ReviewActivitySection review={review} zoom />
    </>
  )
}

/* ── Agency Reviews (wraps the existing review list in its own titled,
   collapsible section — a sibling of Plan Check Revisions / Permit Revisions
   under the Permit, matching the pre-existing Softr hierarchy) ───────────── */

function AgencyReviewsSection({ permitId, reviews }) {
  const key = 'permit-agency-reviews:' + permitId
  const isCollapsed = wf.collapsed[key] !== false
  const myReviews = reviews ? reviews.filter(r => r.permitId === permitId) : null
  const menu = useActionMenu()

  function toggleCollapse(e) {
    e.stopPropagation()
    wf.collapsed[key] = !isCollapsed
    window.dispatchEvent(new Event('outline-render'))
  }
  function handleAddReview() {
    window.dispatchEvent(new CustomEvent('open-add-agency-review-modal', { detail: { permitId } }))
  }

  return (
    <div className={`wf-node wf-node--section${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--section"></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Agency Reviews</span>
          {Array.isArray(myReviews) && myReviews.length > 0 && (
            <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: 8 }}>{myReviews.length}</span>
          )}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={handleAddReview}>Add agency review</ActionMenuItem>
      </ActionMenu>
      {!isCollapsed && (
        <div className="wf-children">
          {myReviews === null && loadingRow('loading')}
          {Array.isArray(myReviews) && myReviews.length === 0 && loadingRow('No agency reviews for this permit yet.')}
          {Array.isArray(myReviews) && myReviews.map(r => <AgencyReviewNode key={r.id} review={r} />)}
        </div>
      )}
    </div>
  )
}

/* ── Permit node ────────────────────────────────────────────────────────────
   Canonical Permit row (One-Component-Per-Node-Type) — exported for reuse under
   permit-related milestones (MilestoneSections.jsx's "Permits" branch, Mount A),
   unfiltered by any milestone linkage: it's simply every permit on the project,
   reusing the same ctx.permitCache/permitReviewCache/permitDeltaCache this file
   owns and keeps fresh. ────────────────────────────────────────────────────── */

export function PermitNode({ permit, reviews, deltas }) {
  const key = 'permit:' + permit.id
  const isCollapsed = wf.collapsed[key] !== false
  const menu = useActionMenu()

  function toggleCollapse(e) {
    e.stopPropagation()
    wf.collapsed[key] = !isCollapsed
    window.dispatchEvent(new Event('outline-render'))
  }

  function handleEdit() {
    window.dispatchEvent(new CustomEvent('open-add-permit-modal', { detail: {
      permitId: permit.id,
      prefill: { typeOfPermit: permit.type, subProjectName: permit.subProjectName, trackingNumber: permit.trackingNumber, estimatedApproval: permit.estimatedApproval },
    } }))
  }
  function handleDelete() {
    confirmDelete('Delete this permit and its agency reviews?', () => deletePermit(permit.id), () => {
      ctx.permitCache = undefined
      ctx.permitReviewCache = undefined
    })
  }

  return (
    <div className={`wf-node wf-node--phase${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggleCollapse}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--phase" title="Zoom into permit"
          onClick={e => { e.stopPropagation(); ctx.zoomToSection && ctx.zoomToSection('permit-detail:' + permit.id, { root: 'permits' }) }}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ fontWeight: 600 }}>{permit.type || permit.name}</span>
          <span className="wf-meta">
            {permit.hasBall && <SoccerBall />}
            {permit.status && (
              <span className="wf-status-pill" style={{ color: 'var(--aj-text)', borderColor: 'var(--aj-border-mid)' }}>
                {permit.status}
              </span>
            )}
            {permit.trackingNumber && (
              <span className="wf-date-btn" style={{ pointerEvents: 'none', color: 'var(--aj-text-muted)' }}>#{permit.trackingNumber}</span>
            )}
            {permit.estimatedApproval && !permit.issueDate && (
              <span className="wf-date-btn" style={{ pointerEvents: 'none', color: 'var(--aj-text-muted)' }} title="Estimated approval">
                est. {fmtDate(permit.estimatedApproval)}
              </span>
            )}
          </span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={handleEdit}>Edit permit</ActionMenuItem>
        {permit.webPortalUrl && <ActionMenuItem onClick={() => window.open(permit.webPortalUrl, '_blank')}>Open web portal</ActionMenuItem>}
        <ActionMenuDivider />
        <ActionMenuItem onClick={handleDelete} danger>Delete permit</ActionMenuItem>
      </ActionMenu>
      {!isCollapsed && (
        <div className="wf-children">
          <AgencyReviewsSection permitId={permit.id} reviews={reviews} />
          <DeltaRevisionsSection
            permitId={permit.id} deltas={deltas} reviews={reviews}
            type="Plan Check Revision" label="Plan Check Revisions"
            collapseKeyPrefix="permit-pc-revisions"
          />
          <DeltaRevisionsSection
            permitId={permit.id} deltas={deltas} reviews={reviews}
            type="Permit Revision" label="Permit Revisions"
            collapseKeyPrefix="permit-permit-revisions"
          />
        </div>
      )}
    </div>
  )
}

/* ── List content (shared home + rootZoom) ─────────────────────────────── */

function PermitListContent({ permits, reviews, deltas }) {
  if (permits === null || permits === undefined) return loadingRow('loading')
  if (permits.length === 0) return loadingRow('empty')
  return permits.map(p => <PermitNode key={p.id} permit={p} reviews={reviews} deltas={deltas} />)
}

/* ── Main component ─────────────────────────────────────────────────────── */

export default function PermitsSection() {
  const [, forceUpdate]         = useReducer(v => v + 1, 0)
  const [vis, setVis]           = useState(calcPermitsVis)
  const [recordId, setRecordId] = useState(() => wf.zoomProjectId || null)

  function ensureLoaded(projId) {
    if (!projId) return
    if (ctx.permitCache === undefined) {
      ctx.permitCache = null
      getProjectPermits(projId).then(p => {
        ctx.permitCache = p
        window.dispatchEvent(new Event('outline-render'))
      }).catch(() => {
        ctx.permitCache = []
        window.dispatchEvent(new Event('outline-render'))
      })
    }
    if (ctx.permitReviewCache === undefined) {
      ctx.permitReviewCache = null
      getProjectAgencyReviews(projId).then(r => {
        ctx.permitReviewCache = r
        window.dispatchEvent(new Event('outline-render'))
        loadReviewClocks(r.map(x => x.id)).catch(() => {})
      }).catch(() => {
        ctx.permitReviewCache = []
        window.dispatchEvent(new Event('outline-render'))
      })
    }
    if (ctx.permitDeltaCache === undefined) {
      ctx.permitDeltaCache = null
      getDeltaRevisionsForProject(projId).then(d => {
        ctx.permitDeltaCache = d
        window.dispatchEvent(new Event('outline-render'))
      }).catch(() => {
        ctx.permitDeltaCache = []
        window.dispatchEvent(new Event('outline-render'))
      })
    }
  }

  // Reset + reload on project change
  useEffect(() => {
    if (!recordId) return
    ctx.permitCache = undefined
    ctx.permitReviewCache = undefined
    ctx.permitDeltaCache = undefined
    ctx.permitActivityCache = {}
    ctx.portalCache = {}
    ctx.pcLetterCache = {}
    ctx.stakeholderCache = {}
    ensureLoaded(recordId)
  }, [recordId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to outline-render for visibility + recordId + cache-bust reloads
  useEffect(() => {
    function onRender() {
      const newRecordId = wf.zoomProjectId || null
      setRecordId(prev => (prev !== newRecordId ? newRecordId : prev))
      const v = calcPermitsVis()
      setVis(v)
      if (v !== 'hidden') ensureLoaded(newRecordId)
      forceUpdate()
    }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (vis === 'hidden') return null

  const permits = ctx.permitCache
  const reviews = ctx.permitReviewCache
  const deltas  = ctx.permitDeltaCache
  const rootCollapsed = wf.collapsed['root:permits'] !== false

  /* ── rootZoom ── (count now shown on the ZoomTitle line) */
  if (vis === 'rootZoom') {
    return (
      <div className="wf-list">
        <PermitListContent permits={permits} reviews={reviews} deltas={deltas} />
      </div>
    )
  }

  /* ── permitZoom — children-only: Agency Reviews / Plan Check Revisions / Permit Revisions ── */
  if (vis === 'permitZoom') {
    const permitId = (wf.zoomSection || '').split(':')[1] || ''
    return (
      <div className="wf-list">
        <AgencyReviewsSection permitId={permitId} reviews={reviews} />
        <DeltaRevisionsSection
          permitId={permitId} deltas={deltas} reviews={reviews}
          type="Plan Check Revision" label="Plan Check Revisions"
          collapseKeyPrefix="permit-pc-revisions"
        />
        <DeltaRevisionsSection
          permitId={permitId} deltas={deltas} reviews={reviews}
          type="Permit Revision" label="Permit Revisions"
          collapseKeyPrefix="permit-permit-revisions"
        />
      </div>
    )
  }

  /* ── reviewZoom — children-only: portal + activity log ── */
  if (vis === 'reviewZoom') {
    const reviewId = (wf.zoomSection || '').split(':')[1] || ''
    const review = Array.isArray(reviews) ? reviews.find(r => r.id === reviewId) : null
    if (!review) return <div className="wf-list">{loadingRow('loading')}</div>
    return (
      <div className="wf-list">
        <ReviewZoomContent review={review} />
      </div>
    )
  }

  /* ── home / referenceZoom — Reference-child root node ── */
  // At project level the root row renders only when Reference is expanded
  // (indented); in Reference zoom it renders plain.
  if (vis === 'home' && wf.collapsed['root:reference'] !== false) return null
  const rootNode = (
      <div
        className={`wf-node wf-node--root wf-node--root-permits${rootCollapsed ? ' wf-node--collapsed' : ''}`}
        data-key="root:permits"
      >
        <div className="wf-row">
          <button
            className="wf-triangle"
            onClick={e => {
              e.stopPropagation()
              const next = !rootCollapsed
              wf.collapsed['root:permits'] = next
              saveViewState()
              if (!next) ensureLoaded(recordId)
              window.dispatchEvent(new Event('outline-render'))
            }}
          >
            <span className={`wf-tri-icon ${rootCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
          </button>
          <button
            className="wf-bullet wf-bullet--root"
            title="Focus on Permits"
            data-fn="zoomTo"
            data-level="root"
            data-rootname="permits"
          ></button>
          <div className="wf-text-wrap">
            <span className="wf-node-text wf-node-text--root">Permits</span>
            {Array.isArray(permits) && permits.length > 0 && (
              <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: 8 }}>{permits.length}</span>
            )}
          </div>
          <button
            className="wf-menu-btn"
            data-fn="openMenu"
            data-type="root"
            data-key="root:permits"
            data-rootname="permits"
          >&#8943;</button>
        </div>
        {!rootCollapsed && (
          <div className="wf-children">
            <PermitListContent permits={permits} reviews={reviews} deltas={deltas} />
          </div>
        )}
      </div>
  )

  if (vis === 'referenceZoom') return <div className="wf-list">{rootNode}</div>
  return <div className="wf-list"><div className="wf-ref-child">{rootNode}</div></div>
}
